import { useState } from "react";
import type { CalendarEdit, EditProposal, EditSnapshot } from "@habitos/shared";
import { describeDays, formatDay, formatDuration } from "../lib/format";

interface Props {
  proposal: EditProposal;
  busy: boolean;
  onAccept: (indexes: number[]) => void;
  onRetry: () => void;
}

/** A long list shows its first changes; the rest open on demand. */
const FIRST_EDITS = 20;

/** Shows the changes the assistant proposed to what is already on the calendar. */
export function EditReview({ proposal, busy, onAccept, onRetry }: Props) {
  const [off, setOff] = useState<Set<number>>(new Set());
  const [all, setAll] = useState(false);
  const chosen = proposal.edits.map((_, i) => i).filter((i) => !off.has(i));
  const shown = all ? proposal.edits : proposal.edits.slice(0, FIRST_EDITS);

  const toggle = (i: number) =>
    setOff((cur) => {
      const next = new Set(cur);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <div className="stack">
      <section className="card">
        <p className="summary">{proposal.summary}</p>
      </section>
      <ul className="list">
        {shown.map((edit, i) => (
          <li key={edit.id}>
            <label className={`step card ${off.has(i) ? "off" : ""}`}>
              <input type="checkbox" checked={!off.has(i)} onChange={() => toggle(i)} />
              <div className="grow">
                <div className="habit-title">
                  {edit.after ? "✏️" : "🗑️"} {edit.before.emoji} {edit.before.title}
                </div>
                <div className="muted small">{edit.after ? changes(edit) : `Quitar ${edit.kind === "habit" ? "este hábito" : "esta tarea"}`}</div>
              </div>
            </label>
          </li>
        ))}
      </ul>
      {shown.length < proposal.edits.length && (
        <button className="btn" onClick={() => setAll(true)}>
          Ver los {proposal.edits.length - shown.length} cambios restantes
        </button>
      )}
      <div className="row gap">
        <button className="btn" onClick={onRetry} disabled={busy}>Otra propuesta</button>
        <button className="btn primary grow" disabled={busy || chosen.length === 0} onClick={() => onAccept(chosen)}>
          {busy ? "Aplicando…" : chosen.length === 1 ? "Aplicar el cambio" : `Aplicar ${chosen.length} cambios`}
        </button>
      </div>
    </div>
  );
}

/** "09:00 → 10:00 · 2 h → 3 h": only what changes. */
function changes({ before, after }: CalendarEdit): string {
  const a = after as EditSnapshot;
  const parts: string[] = [];
  const diff = (label: (s: EditSnapshot) => string) => {
    if (label(before) !== label(a)) parts.push(`${label(before)} → ${label(a)}`);
  };
  diff((s) => s.title);
  diff((s) => (s.date ? formatDay(s.date) : ""));
  diff((s) => (s.daysOfWeek ? describeDays(s.daysOfWeek) : ""));
  diff((s) => s.time ?? "Sin hora");
  diff((s) => formatDuration(s.durationMinutes));
  diff((s) => (s.startDate ? `${formatDay(s.startDate)} → ${s.endDate ? formatDay(s.endDate) : "en adelante"}` : ""));
  return parts.join(" · ");
}
